import { List } from 'immutable'
import { createSelector } from 'reselect'
import { pick, identity, map } from 'ramda'
import { flipProp } from 'utils/functional'
import { generateEcuListItem } from 'utils/circuits'
import { getInOr, safeToJS, getIn } from 'utils/immutable'
import { ECU_BUTTON_MODULES } from 'utils/constants'

const getCtpState = (state) => state.get('ctp')

export const getCtp = createSelector(getCtpState, (ctpState) => ctpState.toJS())

export const availableCircuitsByRP = (state, rpUri) =>
  getInOr(List(), ['ctp', 'availableCircuits', rpUri], state)

export const getAvailableCircuitsByRP = createSelector(
  availableCircuitsByRP,
  safeToJS
)

export const availableCircuits = (state) =>
  getInOr(List(), ['ctp', 'availableCircuits'], state)

export const getAvailableCircuits = createSelector(availableCircuits, safeToJS)

const ctpAssignments = (state) => getInOr({}, ['ctp', 'assignments'], state)

export const getCTPAssignments = createSelector(ctpAssignments, safeToJS)

const selectDetailedCircuitById = (state, { id }) =>
  getIn(['ctp', 'detailedCircuits', id])(state)

export const getDetailedCircuitById = createSelector(
  selectDetailedCircuitById,
  safeToJS
)

const getDetailedBundles = (state) =>
  state.getIn(['ctp', 'detailedBundles'], {})

const getAllDetailedBundles = createSelector(
  [getDetailedBundles],
  (detailedBundles) => detailedBundles
)

export const getDetailedBundleById = (id, state) => {
  const detailedBundleState = getAllDetailedBundles(state)
  const selectedBundle = detailedBundleState.get(id)
  if (!selectedBundle) return null
  return selectedBundle.toJS()
}

const selectVehicleModuleUris = (state, props) =>
  state.getIn(['ctp', 'modules', props.serialNumber, 'modules'])
const selectVehicleCTPDictionary = (state, props) =>
  state.getIn(['ctp', 'modules', props.serialNumber])
export const getVehicleModules = createSelector(
  selectVehicleModuleUris,
  selectVehicleCTPDictionary,
  (uris, dictionary) => {
    if (!uris || !dictionary) {
      return []
    }
    return safeToJS(uris.map((uri) => safeToJS(dictionary.get(uri))))
  }
)

export const getVehicleCtpDictionary = createSelector(
  selectVehicleCTPDictionary,
  (dictionary) => safeToJS(dictionary, null)
)

export const getECUModulesButtons = createSelector(
  getVehicleModules,
  (modules) => {
    const mappedModules = modules.map(generateEcuListItem)
    return mappedModules.filter((mod) => ECU_BUTTON_MODULES.includes(mod.value))
  }
)

const targetPin = (state) => {
  const pin = safeToJS(getInOr({}, ['ctp', 'targetPin'], state))

  if (!pin.key) {
    pin.key = `${pin.location}-${pin.connector}-${pin.module}`
  }
  return pin
}

export const getTargetPin = createSelector(targetPin, (pin) => pin)

export const selectedPin = (state) => {
  const pin = safeToJS(getInOr({}, ['ctp', 'selectedPin'], state))

  if (!pin.key) {
    pin.key = `${pin.location}-${pin.connector}-${pin.module}`
  }
  return pin
}

export const getSelectedPin = createSelector(selectedPin, (pin) => pin)

const getSelectedCircuit = (state) =>
  safeToJS(state.getIn(['ctp', 'selectedCircuit'], {}))

const selectCircuits = (state) =>
  state
    .getIn(['ctp', 'ctpReport', 'asBuilt', 'ctp'], List())
    .map(({ circuitNumber }) => circuitNumber)
    .toJS()

export const getAsBuiltCircuits = createSelector([selectCircuits], identity)

export const getAllAsBuiltCircuits = createSelector(getCtpState, (ctp) =>
  ctp.getIn(['ctpReport', 'asBuilt', 'ctp'], []).toJS()
)

export const getModuleInfo = createSelector(
  [
    getVehicleCtpDictionary,
    getSelectedCircuit,
    getTargetPin,
    (__, props) => props,
  ],
  (moduleDictionary, selectedCircuit, targetPinFromState, { urlParams }) => {
    const { moduleName, connectorName, location, id } = urlParams

    const connector = moduleDictionary[`/${moduleName}/${connectorName}`]

    if (!connector) return {}

    const { pins } = connector

    if (!pins) return {}

    const hasCharLocation = !!pins[0].match(/\/.+\/.+\/[a-zA-Z]/)
    const connectorPinUris = hasCharLocation ? pins.sort() : pins
    const connectorPins = map(flipProp(moduleDictionary))(connectorPinUris)

    const selectedPinFromModuleDict = location
      ? moduleDictionary[`/${moduleName}/${connectorName}/${location}`]
      : {}
    const selectedModuleObj = {
      moduleName,
      connectors: [],
    }
    const selectedConnectorObj = {
      connectorName,
      pins: connectorPins,
    }
    const selectedPinObj = {
      ...selectedPinFromModuleDict,
      serialNumber: id,
      ...(selectedPinFromModuleDict.features && {
        features: selectedPinFromModuleDict.features,
      }),
    }

    const modules = {
      selectedModule: selectedModuleObj,
      selectedConnector: selectedConnectorObj,
      selectedPin: selectedPinObj,
      selectedCircuit,
      targetPin: targetPinFromState,
    }

    return modules
  }
)

export const makeGetVehicleModulePinCount = () =>
  createSelector(
    getVehicleCtpDictionary,
    (_, props) => props,
    (ctpDictionary, props) => {
      if (!ctpDictionary) {
        return {}
      }
      const { moduleName } = props
      const module = ctpDictionary[`/${moduleName}`]

      if (!module) {
        return {}
      }
      return pick(['pinCount', 'pinsAvailable'], module)
    }
  )

export const moduleConfigurationSelector = (state, props) =>
  state.getIn(['ctp', 'moduleConfiguration', props.serialNumber, 'modules'])

export const getModuleConfigurationByVehicle = createSelector(
  moduleConfigurationSelector,
  safeToJS
)
